import React from "react";
import { useQuery } from "@tanstack/react-query";
import { History, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface AuditEntry {
  audit_id: string;
  kind: "card" | "provider";
  card_id: string | null;
  provider_id: string | null;
  from_status: string | null;
  to_status: string | null;
  action: "set" | "clear";
  actor_user_id: string;
  actor_email: string;
  note: string;
  created_at: string;
}

interface AuditResp {
  items: AuditEntry[];
  total_24h: number;
  disables_24h: number;
  checked_at: string;
}

const fmtRel = (iso: string): string => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const min = 60_000, hr = 3_600_000, day = 86_400_000;
    if (diff < min) return "just now";
    if (diff < hr) return `${Math.round(diff / min)}m ago`;
    if (diff < day) return `${Math.round(diff / hr)}h ago`;
    return `${Math.round(diff / day)}d ago`;
  } catch { return iso; }
};

const statusChip = (s: string | null) => {
  if (!s) return <span className="font-mono text-[11px] text-[color:var(--sg-muted)]">null</span>;
  const map: Record<string, string> = {
    live: "text-emerald-700 bg-emerald-50 border-emerald-200",
    coming_soon: "text-amber-800 bg-amber-50 border-amber-200",
    disabled: "text-rose-700 bg-rose-50 border-rose-200",
    deprecated: "text-slate-600 bg-slate-100 border-slate-200",
  };
  return (
    <span className={`sg-chip border ${map[s] || map.deprecated}`}>
      {s === "coming_soon" ? "Coming soon" : s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
};

/**
 * CardAuditLog — governance-friendly audit trail of every card/provider
 * status flip performed via Admin Setup. Rendered under the API Errors tab.
 */
export const CardAuditLog: React.FC = () => {
  const q = useQuery<AuditResp>({
    queryKey: ["card-audit"],
    queryFn: async () => (await api.get("/admin/audit/card-status?limit=100")).data,
    refetchInterval: 30_000,
  });

  return (
    <div
      className="rounded-xl border border-[color:var(--sg-border)] bg-white p-4 mt-4"
      data-testid="card-audit-log"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-[color:var(--sg-violet)]" />
          <h3 className="text-sm font-semibold text-[color:var(--sg-fg)]">
            Card lifecycle audit
          </h3>
          {q.data && (
            <>
              <span className="sg-chip bg-slate-100 border-slate-200 text-slate-600"
                    data-testid="audit-total-24h">
                {q.data.total_24h} changes · 24 h
              </span>
              {q.data.disables_24h > 0 && (
                <span className="sg-chip bg-rose-50 border-rose-200 text-rose-700"
                        data-testid="audit-disables-24h">
                  {q.data.disables_24h} disabled · 24 h
                </span>
              )}
            </>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => q.refetch()}
          disabled={q.isFetching}
          data-testid="audit-refresh"
          className="h-7 text-xs"
        >
          {q.isFetching ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3 mr-1" />
          )}
          Refresh
        </Button>
      </div>
      <p className="text-xs text-[color:var(--sg-muted)] mb-3">
        Every Live / Coming soon / Disabled flip made by an admin. Retained 90 days.
      </p>

      {q.isLoading || !q.data ? (
        <div className="sg-skeleton h-32" />
      ) : q.data.items.length === 0 ? (
        <div className="text-xs text-[color:var(--sg-muted)] border border-dashed border-[color:var(--sg-border)] rounded-md p-3"
              data-testid="audit-empty">
          No status changes yet. Head to the OAuth tab and flip a provider or card.
        </div>
      ) : (
        <div className="border border-[color:var(--sg-border)] rounded-md overflow-hidden divide-y divide-[color:var(--sg-border)]">
          {q.data.items.map((e) => (
            <div
              key={e.audit_id}
              data-testid={`audit-entry-${e.audit_id}`}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-center px-3 py-2.5"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`sg-chip border ${
                      e.kind === "card"
                        ? "bg-violet-50 border-violet-200 text-violet-700"
                        : "bg-sky-50 border-sky-200 text-sky-700"
                    }`}
                  >
                    {e.kind}
                  </span>
                  <span className="font-mono text-xs text-[color:var(--sg-fg)]">
                    {e.card_id || e.provider_id}
                  </span>
                  {e.action === "clear" && (
                    <span className="sg-chip bg-slate-100 border-slate-200 text-slate-600">
                      cleared override
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">
                  by <b>{e.actor_email || e.actor_user_id}</b>
                  {e.note && <> · &ldquo;{e.note}&rdquo;</>}
                </div>
              </div>
              <div className="flex items-center gap-1 justify-self-start md:justify-self-end">
                {statusChip(e.from_status)}
                <span className="text-[color:var(--sg-muted)]">→</span>
                {statusChip(e.to_status)}
              </div>
              <div className="text-[11px] text-[color:var(--sg-muted)] whitespace-nowrap">
                {fmtRel(e.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CardAuditLog;
